import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MagneticButton from "./MagneticButton";
import { HOME_BANNER_VIDEO, HOME_BANNER_VIDEO_REVERSE } from "../../config/site";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.15 + i * 0.08, duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  }),
};

const END_HOLD_MS = 1500;

export default function HomeHeroPremium() {
  const forwardRef = useRef(null);
  const reverseRef = useRef(null);

  useEffect(() => {
    const forward = forwardRef.current;
    const reverse = reverseRef.current;
    if (!forward || !reverse) return undefined;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      forward.pause();
      reverse.pause();
      return undefined;
    }

    let holdTimer = null;

    const safePlay = (video) => {
      const p = video.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          /* autoplay blocked */
        });
      }
    };

    const setActive = (which) => {
      const isReverse = which === "reverse";
      forward.classList.toggle("home-hero-video__media--active", !isReverse);
      reverse.classList.toggle("home-hero-video__media--active", isReverse);
    };

    const playForward = () => {
      reverse.pause();
      forward.currentTime = 0;
      const onPlaying = () => {
        forward.removeEventListener("playing", onPlaying);
        setActive("forward");
      };
      forward.addEventListener("playing", onPlaying);
      safePlay(forward);
    };

    const playReverse = () => {
      forward.pause();
      reverse.currentTime = 0;
      const onPlaying = () => {
        reverse.removeEventListener("playing", onPlaying);
        setActive("reverse");
      };
      reverse.addEventListener("playing", onPlaying);
      safePlay(reverse);
    };

    const scheduleReverse = () => {
      if (holdTimer != null) return;
      holdTimer = window.setTimeout(() => {
        holdTimer = null;
        playReverse();
      }, END_HOLD_MS);
    };

    const scheduleForward = () => {
      if (holdTimer != null) return;
      holdTimer = window.setTimeout(() => {
        holdTimer = null;
        playForward();
      }, END_HOLD_MS);
    };

    forward.addEventListener("ended", scheduleReverse);
    reverse.addEventListener("ended", scheduleForward);

    setActive("forward");
    safePlay(forward);

    return () => {
      if (holdTimer != null) {
        clearTimeout(holdTimer);
      }
      forward.removeEventListener("ended", scheduleReverse);
      reverse.removeEventListener("ended", scheduleForward);
    };
  }, []);

  return (
    <section className="home-hero-video" aria-label="Welcome">
      <video
        ref={forwardRef}
        className="home-hero-video__media home-hero-video__media--active"
        src={HOME_BANNER_VIDEO}
        autoPlay
        muted
        playsInline
        preload="auto"
      />
      <video
        ref={reverseRef}
        className="home-hero-video__media"
        src={HOME_BANNER_VIDEO_REVERSE}
        muted
        playsInline
        preload="auto"
      />

      <div className="home-hero-video__overlay" aria-hidden />

      <div className="home-hero-video__content">
        <motion.div className="home-hero-video__copy" initial="hidden" animate="show">
          <motion.p custom={0} variants={fade} className="home-hero-video__eyebrow">
            Rencin Gifting · Curated with care
          </motion.p>
          <motion.h1 custom={1} variants={fade} className="home-hero-video__title">
            Gifts that feel as good to give as to receive
          </motion.h1>
          <motion.p custom={2} variants={fade} className="home-hero-video__sub">
            Magnetic gift boxes, festive hampers, and personalised touches — delivered across
            India with secure checkout.
          </motion.p>
          <motion.div custom={3} variants={fade} className="home-hero-video__ctas">
            <MagneticButton>
              <Link to="/shop" className="lux-btn lux-btn--primary home-hero-video__cta">
                Shop gift boxes
              </Link>
            </MagneticButton>
            <MagneticButton>
              <Link to="/customise" className="lux-btn lux-btn--ghost home-hero-video__cta">
                Build your box
              </Link>
            </MagneticButton>
          </motion.div>
          <motion.p custom={4} variants={fade} className="home-hero-video__hint">
            <a href="#magnetic-difference">See the magnetic unboxing difference</a>
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}
